'use client';

import { cn } from '@/lib/utils/cn';
import {
  Code2,
  Copy,
  Rocket,
  Zap,
  Database,
  Brain,
  Shield,
  Palette,
} from 'lucide-react';
import { useState } from 'react';

export interface CardFlipProps {
  title?: string;
  subtitle?: string;
  description?: string;
  features?: string[];
  linkUrl?: string;

  theme?: 'primary' | 'emerald' | 'orange' | 'secondary' | 'rose';
}

export default function CardFlip({
  title = 'Build MVPs Fast',
  subtitle = 'Launch your idea in record time',
  description = 'Copy, paste, customize—and launch your MVP faster than ever with our developer-first component library.',
  features = [
    'Copy & Paste Ready',
    'Developer-First',
    'MVP Optimized',
    'Zero Setup Required',
  ],

  theme = 'primary',
}: CardFlipProps) {
  const [isFlipped, setIsFlipped] = useState(false);

  const toggleCard = () => {
    setIsFlipped(!isFlipped);
  };

  const themeClasses = {
    primary: {
      gradient:
        'from-primary/5 dark:from-primary/10 via-primary/8 to-primary/5 dark:to-primary/10',
      iconBg: 'from-primary via-primary/90 to-primary/80',
      codeGradient: 'from-primary/20 via-primary/30 to-primary/20',
      featureBg: 'bg-primary/10 dark:bg-primary/20',
      featureIcon: 'text-primary',
      hoverGradient:
        'hover:from-primary/10 hover:via-primary/5 hover:to-transparent dark:hover:from-primary/20 dark:hover:via-primary/10 dark:hover:to-transparent',
      hoverText: 'group-hover/start:text-primary',
      arrow: 'text-primary',
      border:
        'group-hover:border-primary/20 dark:group-hover:border-primary/30',
    },
    emerald: {
      gradient:
        'from-emerald-500/5 dark:from-emerald-500/10 via-emerald-500/8 to-emerald-500/5 dark:to-emerald-500/10',
      iconBg: 'from-emerald-500 via-emerald-500/90 to-emerald-500/80',
      codeGradient: 'from-emerald-500/20 via-emerald-500/30 to-emerald-500/20',
      featureBg: 'bg-emerald-500/10 dark:bg-emerald-500/20',
      featureIcon: 'text-emerald-600 dark:text-emerald-400',
      hoverGradient:
        'hover:from-emerald-500/10 hover:via-emerald-500/5 hover:to-transparent dark:hover:from-emerald-500/20 dark:hover:via-emerald-500/10 dark:hover:to-transparent',
      hoverText:
        'group-hover/start:text-emerald-600 dark:group-hover/start:text-emerald-400',
      arrow: 'text-emerald-600 dark:text-emerald-400',
      border:
        'group-hover:border-emerald-500/20 dark:group-hover:border-emerald-500/30',
    },
    orange: {
      gradient:
        'from-orange-500/5 dark:from-orange-500/10 via-orange-500/8 to-orange-500/5 dark:to-orange-500/10',
      iconBg: 'from-orange-500 via-orange-500/90 to-orange-500/80',
      codeGradient: 'from-orange-500/20 via-orange-500/30 to-orange-500/20',
      featureBg: 'bg-orange-500/10 dark:bg-orange-500/20',
      featureIcon: 'text-orange-600 dark:text-orange-400',
      hoverGradient:
        'hover:from-orange-500/10 hover:via-orange-500/5 hover:to-transparent dark:hover:from-orange-500/20 dark:hover:via-orange-500/10 dark:hover:to-transparent',
      hoverText:
        'group-hover/start:text-orange-600 dark:group-hover/start:text-orange-400',
      arrow: 'text-orange-600 dark:text-orange-400',
      border:
        'group-hover:border-orange-500/20 dark:group-hover:border-orange-500/30',
    },
    secondary: {
      gradient:
        'from-secondary/5 dark:from-secondary/10 via-secondary/8 to-secondary/5 dark:to-secondary/10',
      iconBg: 'from-secondary via-secondary/90 to-secondary/80',
      codeGradient: 'from-secondary/20 via-secondary/30 to-secondary/20',
      featureBg: 'bg-secondary/10 dark:bg-secondary/20',
      featureIcon: 'text-secondary dark:text-secondary',
      hoverGradient:
        'hover:from-secondary/10 hover:via-secondary/5 hover:to-transparent dark:hover:from-secondary/20 dark:hover:via-secondary/10 dark:hover:to-transparent',
      hoverText:
        'group-hover/start:text-secondary dark:group-hover/start:text-secondary',
      arrow: 'text-secondary dark:text-secondary',
      border:
        'group-hover:border-secondary/20 dark:group-hover:border-secondary/30',
    },
    rose: {
      gradient:
        'from-rose-500/5 dark:from-rose-500/10 via-rose-500/8 to-rose-500/5 dark:to-rose-500/10',
      iconBg: 'from-rose-500 via-rose-500/90 to-rose-500/80',
      codeGradient: 'from-rose-500/20 via-rose-500/30 to-rose-500/20',
      featureBg: 'bg-rose-500/10 dark:bg-rose-500/20',
      featureIcon: 'text-rose-600 dark:text-rose-400',
      hoverGradient:
        'hover:from-rose-500/10 hover:via-rose-500/5 hover:to-transparent dark:hover:from-rose-500/20 dark:hover:via-rose-500/10 dark:hover:to-transparent',
      hoverText:
        'group-hover/start:text-rose-600 dark:group-hover/start:text-rose-400',
      arrow: 'text-rose-600 dark:text-rose-400',
      border:
        'group-hover:border-rose-500/20 dark:group-hover:border-rose-500/30',
    },
  };

  const currentTheme = themeClasses[theme];

  return (
    <div
      className="group relative h-full w-full cursor-pointer"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      onClick={toggleCard}
    >
      <div
        className={cn(
          'relative h-full w-full',
          '[transform-style:preserve-3d]',
          'transition-all duration-700',
          isFlipped
            ? '[transform:rotateY(180deg)]'
            : '[transform:rotateY(0deg)]',
        )}
      >
        {/* Front of card */}
        <div
          className={cn(
            'absolute inset-0 w-full',
            '[transform:rotateY(0deg)] [backface-visibility:hidden]',
            'overflow-hidden rounded-2xl',
            'bg-gradient-to-br from-white via-slate-50 to-slate-100',
            'dark:from-zinc-900 dark:via-zinc-900/95 dark:to-zinc-800',
            'border border-slate-200 dark:border-zinc-800/50',
            'shadow-lg dark:shadow-xl',
            'transition-all duration-700',
            'group-hover:shadow-xl dark:group-hover:shadow-2xl',
            currentTheme.border,
            isFlipped ? 'opacity-0' : 'opacity-100',
          )}
        >
          {/* Background gradient effect */}
          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-br',
              currentTheme.gradient,
            )}
          />

          {/* Animated code blocks */}
          <div className="absolute inset-0 flex items-center justify-center pt-20">
            <div className="relative flex h-[100px] w-[200px] flex-col items-center justify-center gap-2">
              {/* Code blocks animation */}
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-3 w-full rounded-sm',
                    currentTheme.codeGradient,
                    'bg-gradient-to-r',
                    'animate-[slideIn_2s_ease-in-out_infinite]',
                    'opacity-0',
                  )}
                  style={{
                    width: `${[85, 72, 93, 68, 80, 76][i]}%`,
                    animationDelay: `${i * 0.2}s`,
                    marginLeft: `${[5, 12, 2, 18, 8, 15][i]}%`,
                  }}
                />
              ))}

              {/* Central rocket icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className={cn(
                    'h-12 w-12 rounded-xl',
                    'bg-gradient-to-br',
                    currentTheme.iconBg,
                    'flex items-center justify-center',
                    'shadow-lg',
                    'animate-pulse',
                    'transition-all duration-500 group-hover:scale-110 group-hover:rotate-12',
                  )}
                >
                  {theme === 'primary' ? (
                    <Code2 className="h-6 w-6 text-white" />
                  ) : theme === 'emerald' ? (
                    <Database className="h-6 w-6 text-white" />
                  ) : theme === 'orange' ? (
                    <Brain className="h-6 w-6 text-white" />
                  ) : theme === 'secondary' ? (
                    <Shield className="h-6 w-6 text-white" />
                  ) : (
                    <Palette className="h-6 w-6 text-white" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom content */}
          <div className="absolute top-0 right-0 left-0 p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-1.5">
                <div className="text-xl leading-snug font-semibold tracking-tight text-zinc-900 transition-all duration-500 ease-out group-hover:translate-y-[-4px] dark:text-white">
                  {title}
                </div>
                <p className="antonio line-clamp-2 font-serif text-sm tracking-tight text-zinc-600 transition-all delay-[50ms] duration-500 ease-out group-hover:translate-y-[-4px] dark:text-zinc-300">
                  {subtitle}
                </p>
              </div>
              <div className="group/icon relative">
                <div
                  className={cn(
                    'absolute inset-[-8px] rounded-lg transition-opacity duration-300',
                    'from-primary/20 via-primary/10 bg-gradient-to-br to-transparent',
                    'opacity-0 group-hover/icon:opacity-100',
                  )}
                />
                <Zap className="text-primary relative z-10 h-5 w-5 transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:rotate-12" />
              </div>
            </div>
          </div>

          {/* Mobile hint */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 md:hidden">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Tap to flip
            </p>
          </div>
        </div>

        {/* Back of card */}
        <div
          className={cn(
            'absolute inset-0 h-full w-full',
            '[transform:rotateY(180deg)] [backface-visibility:hidden]',
            'rounded-2xl p-5',
            'bg-gradient-to-br from-white via-slate-50 to-slate-100',
            'dark:from-zinc-900 dark:via-zinc-900/95 dark:to-zinc-800',
            'border border-slate-200 dark:border-zinc-800',
            'shadow-lg dark:shadow-xl',
            'flex flex-col',
            'transition-all duration-700',
            'group-hover:shadow-xl dark:group-hover:shadow-2xl',
            currentTheme.border,
            !isFlipped ? 'opacity-0' : 'opacity-100',
          )}
        >
          {/* Background gradient */}
          <div
            className={cn(
              'absolute inset-0 rounded-2xl bg-gradient-to-br',
              currentTheme.gradient,
            )}
          />

          <div className="relative z-10 flex-1 space-y-5">
            <div className="space-y-2">
              <div className="mb-2 flex items-center gap-2">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br',
                    currentTheme.iconBg,
                  )}
                >
                  {theme === 'primary' ? (
                    <Code2 className="h-4 w-4 text-white" />
                  ) : theme === 'emerald' ? (
                    <Database className="h-4 w-4 text-white" />
                  ) : theme === 'orange' ? (
                    <Brain className="h-4 w-4 text-white" />
                  ) : theme === 'secondary' ? (
                    <Shield className="h-4 w-4 text-white" />
                  ) : (
                    <Palette className="h-4 w-4 text-white" />
                  )}
                </div>
                <div className="text-xl leading-snug font-semibold tracking-tight text-zinc-900 transition-all duration-500 ease-out group-hover:translate-y-[-2px] dark:text-white">
                  {title}
                </div>
              </div>
              <p className="antonio line-clamp-2 font-serif text-sm tracking-tight text-zinc-600 transition-all duration-500 ease-out group-hover:translate-y-[-2px] dark:text-zinc-400">
                {description}
              </p>
            </div>

            <div className="space-y-2.5">
              {features.map((feature, index) => {
                const getIconsByTheme = () => {
                  switch (theme) {
                    case 'primary': // Frontend
                      return [Code2, Rocket, Palette, Zap];
                    case 'emerald': // Backend
                      return [Database, Code2, Rocket, Shield];
                    case 'orange': // AI/ML
                      return [Brain, Code2, Zap, Rocket];
                    case 'secondary': // DevOps
                      return [Shield, Rocket, Database, Zap];
                    case 'rose': // UI/UX
                      return [Palette, Copy, Code2, Zap];
                    default:
                      return [Copy, Code2, Rocket, Zap];
                  }
                };
                const icons = getIconsByTheme();
                const IconComponent = icons[index % icons.length];

                return (
                  <div
                    key={feature}
                    className="flex items-center gap-3 text-sm text-zinc-700 transition-all duration-500 dark:text-zinc-300"
                    style={{
                      transform: isFlipped
                        ? 'translateX(0)'
                        : 'translateX(-10px)',
                      opacity: isFlipped ? 1 : 0,
                      transitionDelay: `${index * 100 + 200}ms`,
                    }}
                  >
                    <div
                      className={cn(
                        'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md',
                        currentTheme.featureBg,
                      )}
                    >
                      <IconComponent
                        className={cn('h-3 w-3', currentTheme.featureIcon)}
                      />
                    </div>
                    <span className="font-medium">{feature}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile hint */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 md:hidden">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Tap to flip back
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          50% {
            transform: translateX(0);
            opacity: 0.8;
          }
          100% {
            transform: translateX(100px);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
